import { Directive, TemplateRef, ViewContainerRef, effect, inject, input } from '@angular/core';
import { PermissionStore } from '../../services/permission-service/permission.store';
import { PermissionAction } from '../../models/permission.model';

/**
 * Renders the element only when the signed-in role has the permission.
 *   <button *appHasPermission="menuKeys.Game; action: 'edit'">Edit</button>
 *   <button *appHasPermission="menuKeys.Cards; action: 'export'; else noExport">Export</button>
 * The action defaults to 'view'. An element can carry only one structural directive:
 * wrap an existing *ngIf in <ng-container *appHasPermission="...">.
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: false,
})
export class HasPermissionDirective {
  readonly menuKey = input.required<string>({ alias: 'appHasPermission' });
  readonly action = input<PermissionAction>('view', { alias: 'appHasPermissionAction' });
  readonly elseTemplate = input<TemplateRef<unknown> | null>(null, { alias: 'appHasPermissionElse' });

  private readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly permissionStore = inject(PermissionStore);
  private rendered: boolean | null = null;

  constructor() {
    // Re-runs when the inputs or the loaded permissions change.
    effect(() => {
      const allowed = this.permissionStore.can(this.menuKey(), this.action());
      const elseTemplate = this.elseTemplate();
      if (allowed === this.rendered) {
        return;
      }

      this.viewContainer.clear();
      if (allowed) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      } else if (elseTemplate) {
        this.viewContainer.createEmbeddedView(elseTemplate);
      }
      this.rendered = allowed;
    });
  }
}
